import { AnimatePresence, motion } from "framer-motion";
import { BrandAsset } from "@/components/branding/BrandAsset";
import { BrandLockup } from "@/components/branding/BrandLockup";
import { useTranslation } from "@/packages/utils";
import { AuthLanguageSwitcher } from "./AuthLanguageSwitcher";

interface AuthLayoutProps {
  children: React.ReactNode;
  headline?: string;
  subline?: string;
  wide?: boolean;
  onBrandClick?: () => void;
}

const ease = [0.25, 0.46, 0.45, 0.94] as const;

export function AuthLayout({
  children,
  headline = "AI-powered audit readiness",
  subline = "Streamline compliance preparation with intelligent document management and real-time tracking.",
  wide = false,
  onBrandClick,
}: AuthLayoutProps) {
  const { t } = useTranslation(["auth"]);
  return (
    <div className="flex min-h-svh bg-white">
      {/* ── Left branded panel ── */}
      <div
        className={`hidden lg:flex ${wide ? "lg:w-[38%]" : "lg:w-[55%]"} flex-col bg-[#0a0a0a] relative overflow-hidden select-none`}
      >
        {/* Ambient grid */}
        <div className="absolute inset-0 auth-ambient-grid" />

        {/* Accent glow — bottom-left */}
        <div className="absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-accent/15 blur-[140px] pointer-events-none" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Brand */}
          <motion.button
            type="button"
            onClick={onBrandClick}
            className={`flex items-center gap-2.5 ${onBrandClick ? "cursor-pointer" : "cursor-default"}`}
            disabled={!onBrandClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <BrandLockup
              iconClassName="size-8"
              inverted
              textClassName="text-[18px] font-semibold"
            />
          </motion.button>

          {/* Headline — animated and keyed so it transitions on change */}
          <div className="max-w-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={headline}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45, ease }}
              >
                <h1 className="text-[40px] font-bold leading-[1.08] tracking-[-0.025em] text-white">
                  {headline}
                </h1>
                <p className="mt-5 text-[16px] leading-[1.6] text-white/45">
                  {subline}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Footer */}
          <motion.p
            className="text-[13px] text-white/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            {t("auth:trustedBy")}
          </motion.p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col min-h-svh">
        {/* Top bar: mobile brand + language switcher */}
        <div className="flex items-center justify-between px-6 pt-6 lg:pt-4 lg:pr-6">
          <button
            type="button"
            onClick={onBrandClick}
            className={`lg:hidden flex items-center gap-2.5 ${onBrandClick ? "cursor-pointer" : "cursor-default"}`}
            disabled={!onBrandClick}
          >
            <BrandAsset alt="Audinero" className="size-8" variant="icon" />
          </button>
          <div className="ml-auto">
            <AuthLanguageSwitcher />
          </div>
        </div>

        {/* Form container — centered */}
        <div className="flex-1 flex items-center justify-center px-6 py-8 md:px-12 md:py-10">
          <div className={`w-full ${wide ? "max-w-[560px]" : "max-w-[380px]"}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
